'use client'

import dayjs from 'dayjs'
import { cn } from 'lib/utils'
import { Calendar as CalendarIcon } from 'lucide-react'
import { Button } from 'primitives/button'
import { Popover, PopoverContent, PopoverTrigger } from 'primitives/popover'
import { Select } from 'primitives/select'
import * as React from 'react'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

interface MonthYear {
  month: number
  year: number
}

interface MonthYearRangePickerProps {
  /** Serialized value in "YYYY-MM-DD,YYYY-MM-DD" format */
  value?: string
  /** Callback with serialized "YYYY-MM-DD,YYYY-MM-DD" string */
  onChange?: (value: string) => void
  /** Disable months after the current month */
  disableFutureDates?: boolean
  /** Placeholder text */
  placeholder?: string
  className?: string
}

const currentMonth = dayjs().month()
const currentYear = dayjs().year()

const parseValue = (value?: string): { from?: MonthYear; to?: MonthYear } => {
  if (!value) return {}
  const [fromStr, toStr] = value.split(',')
  const from = fromStr ? dayjs(fromStr) : null
  const to = toStr ? dayjs(toStr) : null
  return {
    from: from?.isValid() ? { month: from.month(), year: from.year() } : undefined,
    to: to?.isValid() ? { month: to.month(), year: to.year() } : undefined,
  }
}

const serialize = (from: MonthYear, to: MonthYear): string => {
  const start = dayjs().year(from.year).month(from.month).startOf('month').format('YYYY-MM-DD')
  const end = dayjs().year(to.year).month(to.month).endOf('month').format('YYYY-MM-DD')
  return `${start},${end}`
}

const formatDisplay = (from?: MonthYear, to?: MonthYear): string | null => {
  if (!from || !to) return null
  const fromStr = `${MONTHS[from.month]} ${from.year}`
  const toStr = `${MONTHS[to.month]} ${to.year}`
  return `${fromStr} - ${toStr}`
}

const isAfter = (a: MonthYear, b: MonthYear) =>
  a.year > b.year || (a.year === b.year && a.month > b.month)

const isSame = (a: MonthYear, b: MonthYear) => a.year === b.year && a.month === b.month

const isMonthDisabled = (month: number, year: number, disableFuture?: boolean) => {
  if (!disableFuture) return false
  return year > currentYear || (year === currentYear && month > currentMonth)
}

const MonthGrid = ({
  selectedMonth,
  year,
  onSelect,
  disableFuture,
}: {
  selectedMonth: number
  year: number
  onSelect: (month: number) => void
  disableFuture?: boolean
}) => (
  <div className="grid grid-cols-4 gap-1">
    {MONTHS.map((name, index) => {
      const disabled = isMonthDisabled(index, year, disableFuture)
      const isSelected = selectedMonth === index
      return (
        <Button
          key={name}
          variant={isSelected ? 'default' : 'ghost'}
          size="sm"
          className="h-8 font-inter text-s-400 normal-case tracking-normal"
          disabled={disabled}
          onClick={() => onSelect(index)}
          data-testid={`month-year-picker-month-${name.toLowerCase()}`}
        >
          {name}
        </Button>
      )
    })}
  </div>
)

const YearSelect = ({
  value,
  onChange,
  disableFuture,
  testIdPrefix,
}: {
  value: number
  onChange: (year: number) => void
  disableFuture?: boolean
  testIdPrefix: string
}) => {
  const minYear = currentYear - 10
  const maxYear = disableFuture ? currentYear : currentYear + 5
  const years = Array.from({ length: maxYear - minYear + 1 }, (_, i) => minYear + i)
  const options = years.map((y) => ({ value: String(y), label: String(y) }))

  return (
    <Select
      value={String(value)}
      onChange={(v) => onChange(Number(v))}
      options={options}
      searchable={false}
      size="sm"
      className="w-full font-jetbrains text-xs-400 normal-case tracking-normal tabular-nums"
      data-testid={`month-year-picker-${testIdPrefix}-year`}
    />
  )
}

export const MonthYearRangePicker = ({
  value,
  onChange,
  disableFutureDates,
  placeholder,
  className,
}: MonthYearRangePickerProps) => {
  const [open, setOpen] = React.useState(false)

  const parsed = parseValue(value)
  const defaultFrom: MonthYear = parsed.from ?? {
    month: dayjs().subtract(2, 'years').month(),
    year: dayjs().subtract(2, 'years').year(),
  }
  const defaultTo: MonthYear = parsed.to ?? { month: currentMonth, year: currentYear }

  const [tempFrom, setTempFrom] = React.useState<MonthYear>(defaultFrom)
  const [tempTo, setTempTo] = React.useState<MonthYear>(defaultTo)

  React.useEffect(() => {
    const p = parseValue(value)
    if (p.from) setTempFrom(p.from)
    if (p.to) setTempTo(p.to)
  }, [value])

  const displayText = formatDisplay(parsed.from, parsed.to)

  const handleFromMonthSelect = (month: number) => {
    const newFrom = { ...tempFrom, month }
    setTempFrom(newFrom)
    if (isAfter(newFrom, tempTo)) {
      setTempTo(newFrom)
    }
  }

  const handleToMonthSelect = (month: number) => {
    const newTo = { ...tempTo, month }
    if (isAfter(tempFrom, newTo)) return
    setTempTo(newTo)
  }

  const handleFromYearChange = (year: number) => {
    const newFrom = { ...tempFrom, year }
    setTempFrom(newFrom)
    if (isAfter(newFrom, tempTo)) {
      setTempTo(newFrom)
    }
  }

  const handleToYearChange = (year: number) => {
    const newTo = { ...tempTo, year }
    if (isAfter(tempFrom, newTo)) {
      setTempTo({ ...newTo, month: tempFrom.month })
    } else {
      setTempTo(newTo)
    }
  }

  const handleApply = () => {
    onChange?.(serialize(tempFrom, tempTo))
    setOpen(false)
  }

  const handleCancel = () => {
    const p = parseValue(value)
    if (p.from) setTempFrom(p.from)
    if (p.to) setTempTo(p.to)
    setOpen(false)
  }

  const isValid = !isAfter(tempFrom, tempTo) || isSame(tempFrom, tempTo)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          data-testid="button-month-year-range-picker"
          variant="outline"
          className={cn(
            // A date range is a value, not an action label — opt out of the
            // Button's mono `label` token so it reads as Inter like the other
            // toolbar filters.
            'w-[300px] justify-start text-left font-inter text-s-400 normal-case tracking-normal',
            !displayText && 'text-muted-foreground',
            className,
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {displayText || <span>{placeholder || 'Select month range'}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-4" align="start">
        <div className="flex gap-6">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <p className="font-inter text-s-500 normal-case tracking-normal text-muted-foreground">
                From
              </p>
              <YearSelect
                value={tempFrom.year}
                onChange={handleFromYearChange}
                disableFuture={disableFutureDates}
                testIdPrefix="from"
              />
            </div>
            <MonthGrid
              selectedMonth={tempFrom.month}
              year={tempFrom.year}
              onSelect={handleFromMonthSelect}
              disableFuture={disableFutureDates}
            />
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <p className="font-inter text-s-500 normal-case tracking-normal text-muted-foreground">
                To
              </p>
              <YearSelect
                value={tempTo.year}
                onChange={handleToYearChange}
                disableFuture={disableFutureDates}
                testIdPrefix="to"
              />
            </div>
            <MonthGrid
              selectedMonth={tempTo.month}
              year={tempTo.year}
              onSelect={handleToMonthSelect}
              disableFuture={disableFutureDates}
            />
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCancel}
            data-testid="month-year-picker-cancel"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleApply}
            disabled={!isValid}
            data-testid="month-year-picker-apply"
          >
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
